import { useState, type ReactNode } from "react";
import { Link } from "@inertiajs/react";
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
} from "@floating-ui/react";

export interface DropdownMenuItem {
  text: string;
  href?: string;
  download?: boolean;
  onClick?: () => void;
  variant?: "default" | "danger";
}

interface DropdownTrigger {
  ref: (node: HTMLElement | null) => void;
  props: Record<string, unknown>;
  open: boolean;
}

interface DropdownMenuProps {
  items: DropdownMenuItem[];
  renderTrigger: (trigger: DropdownTrigger) => ReactNode;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({ items, renderTrigger }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleItemClick = (item: DropdownMenuItem) => {
    item.onClick?.();
    setIsOpen(false);
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {renderTrigger({
        ref: refs.setReference,
        props: getReferenceProps(),
        open: isOpen,
      })}

      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="z-50 min-w-[160px] overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-cream-200 dark:bg-cream-900 dark:ring-cream-700"
        >
          <div className="py-1">
            {items.map((item, index) => {
              const baseClasses =
                "block w-full px-4 py-2 text-left text-sm hover:cursor-pointer hover:bg-cream-100 dark:hover:bg-cream-800";
              const variantClasses =
                item.variant === "danger"
                  ? "text-danger hover:text-danger"
                  : "text-cream-900 dark:text-cream-100";
              const className = `${baseClasses} ${variantClasses}`;

              if (item.href && item.download) {
                return (
                  <a
                    key={index}
                    href={item.href}
                    download
                    className={className}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.text}
                  </a>
                );
              }

              if (item.href) {
                return (
                  <Link
                    key={index}
                    href={item.href}
                    className={className}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.text}
                  </Link>
                );
              }

              return (
                <button
                  key={index}
                  type="button"
                  className={className}
                  onClick={() => handleItemClick(item)}
                >
                  {item.text}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default DropdownMenu;
